import { action } from '@ember/object';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import PowerSelect from 'ember-power-select/components/power-select';
import type { Select } from 'ember-power-select/components/power-select';
import {
  SYSTEM_TIMEZONE_OPTION,
  TIMEZONE_OPTIONS,
  UTC_TIMEZONE_OPTION,
  type TimezoneOption,
} from 'garaje/utils/timezone-options';

export type { TimezoneOption };

interface TimezoneSelectSignature {
  Args: {
    allowClear?: boolean;
    /**
     * Accessible label for the trigger and search input. Defaults to "Select timezone, opens a searchable list".
     * Pass a more specific label when the surrounding context does not include a visible label.
     */
    ariaLabel?: string;
    disabled?: boolean;
    dropdownClass?: string;
    horizontalPosition?: string;
    placeholder?: string;
    /**
     * The city name within the selected timezone zone, used to restore
     * a specific city when a zone has multiple major cities.
     */
    selectedCity?: string | null;
    triggerClass?: string;
    /**
     * The currently selected IANA timezone name (e.g. "America/New_York").
     * Defaults to the system timezone when not provided.
     */
    value?: string | null;
    /**
     * Fires when the user selects a timezone option. Receives the full
     * TimezoneOption object so callers can access ianaName, city, gmtLabel, etc.
     */
    onChange: (option: TimezoneOption) => void;
    /**
     * Fires when the user clears the selection. Only relevant when @allowClear is true.
     */
    onClear?: () => void;
  };
  Element: HTMLDivElement;
}

export default class TimezoneSelect extends Component<TimezoneSelectSignature> {
  @tracked searchText = '';
  @tracked isActive = false;

  get selected(): TimezoneOption {
    const ianaName = this.args.value ?? null;

    if (!ianaName) return SYSTEM_TIMEZONE_OPTION;
    if (ianaName === SYSTEM_TIMEZONE_OPTION.ianaName) return SYSTEM_TIMEZONE_OPTION;
    if (ianaName === UTC_TIMEZONE_OPTION.ianaName) return UTC_TIMEZONE_OPTION;

    const city = this.args.selectedCity ?? null;
    const options = TIMEZONE_OPTIONS;
    const exactMatch = city ? options.find((o) => o.ianaName === ianaName && o.city === city) : null;

    return exactMatch ?? options.find((o) => o.ianaName === ianaName) ?? SYSTEM_TIMEZONE_OPTION;
  }

  get options(): TimezoneOption[] {
    const term = this.searchText.trim().toLowerCase();

    if (!term) {
      return [SYSTEM_TIMEZONE_OPTION, UTC_TIMEZONE_OPTION, ...TIMEZONE_OPTIONS];
    }

    const filtered = TIMEZONE_OPTIONS.filter((o) => o.searchCorpus.includes(term));

    filtered.sort((a, b) => {
      const aAbbr = a.abbreviation.toLowerCase();
      const bAbbr = b.abbreviation.toLowerCase();
      const aCity = a.city.toLowerCase();
      const bCity = b.city.toLowerCase();
      const rank = (abbr: string, city: string): number => {
        if (abbr === term) return 0;
        if (city === term) return 1;
        if (city.startsWith(term)) return 2;
        return 3;
      };

      return rank(aAbbr, aCity) - rank(bAbbr, bCity);
    });

    const systemMatches = SYSTEM_TIMEZONE_OPTION.searchCorpus.includes(term);
    const utcMatches = UTC_TIMEZONE_OPTION.searchCorpus.includes(term);
    const pinned = [...(systemMatches ? [SYSTEM_TIMEZONE_OPTION] : []), ...(utcMatches ? [UTC_TIMEZONE_OPTION] : [])];

    return [...pinned, ...filtered];
  }

  // Passthrough matcher — all filtering is handled by the options getter via _searchText.
  // Without this, PowerSelect attempts its own default filter on object options and throws.
  @action
  matcher(_option: TimezoneOption, _searchText: string): boolean {
    return true;
  }

  @action
  defaultHighlighted({ results }: { results: TimezoneOption[] }): TimezoneOption | undefined {
    return results[0];
  }

  @action
  onInput(term: string, _select: Select, _event: Event): void {
    this.searchText = term;
  }

  gmtAriaLabel(gmtLabel: string): string {
    // Remove trailing :00 so screen readers don't read offsets with "o'clock"
    return gmtLabel.replace('GMT', 'U. T. C.').replace(':00', '');
  }

  cityLabel(option: TimezoneOption): string {
    if (option.isSystemTimezone) return `${option.city} (${option.alternativeName})`;

    return option.countryName ? `${option.city}, ${option.countryName}` : option.city;
  }

  @action
  onFocus(_select: Select, _event: FocusEvent): void {
    this.isActive = true;
  }

  @action
  onBlur(_select: Select, _event: FocusEvent): void {
    this.isActive = false;
  }

  @action
  onChange(option: TimezoneOption | null): void {
    this.searchText = '';

    if (!option) {
      this.args.onClear?.();
      return;
    }

    this.args.onChange(option);
  }

  <template>
    {{#let (this.cityLabel this.selected) as |selectedLabel|}}
      <span class="sr-only" aria-live="polite" aria-atomic="true" data-test-timezone-live-region>
        {{#if this.isActive}}Selected timezone: {{selectedLabel}} {{this.gmtAriaLabel this.selected.gmtLabel}}{{/if}}
      </span>
      <div data-test-timezone-select title={{selectedLabel}} ...attributes>
        <PowerSelect
          @options={{this.options}}
          @selected={{this.selected}}
          @onChange={{this.onChange}}
          @matcher={{this.matcher}}
          @onInput={{this.onInput}}
          @defaultHighlighted={{this.defaultHighlighted}}
          @searchEnabled={{true}}
          @searchPlaceholder="Search by city, timezone, or abbreviation..."
          @ariaLabel={{if @ariaLabel @ariaLabel "Select timezone, opens a searchable list"}}
          @onFocus={{this.onFocus}}
          @onBlur={{this.onBlur}}
          @allowClear={{@allowClear}}
          @disabled={{@disabled}}
          @placeholder={{@placeholder}}
          @dropdownClass="w-96 {{@dropdownClass}}"
          @triggerClass={{@triggerClass}}
          @matchTriggerWidth={{false}}
          @horizontalPosition={{if @horizontalPosition @horizontalPosition "auto"}}
          data-test-timezone-select-trigger
          as |option|
        >
          <div
            class="flex items-baseline justify-between gap-4 px-1 min-w-0"
            data-test-timezone-option="{{option.ianaName}}-{{option.city}}"
          >
            {{#let (this.cityLabel option) as |label|}}
              <span
                class="{{if option.isSystemTimezone 'font-medium text-carbon-80' 'text-carbon-70'}} truncate min-w-0"
                title={{label}}
                data-test-timezone-option-city
              >
                {{#if option.isSystemTimezone}}
                  {{option.city}}
                  <span class="font-normal text-carbon-40 text-sm ml-1" aria-label={{option.alternativeName}}>
                    ({{option.alternativeName}})
                  </span>
                {{else}}
                  {{label}}
                {{/if}}
              </span>
            {{/let}}
            <span
              class="text-carbon-40 text-sm shrink-0"
              aria-label={{this.gmtAriaLabel option.gmtLabel}}
              data-test-timezone-option-gmt
            >
              <span aria-hidden="true">{{option.gmtLabel}}</span>
            </span>
          </div>
        </PowerSelect>
      </div>
    {{/let}}
  </template>
}
